import React, { useState, useEffect, useMemo } from 'react';
import InputElement from '../../Common/InputElement';
import Uploader from '../../Common/Uploader';
import ButtonElement from '../../Common/ButtonElement';
import { useApiRequest } from '../../hooks/useApiRequest';
import { appApis } from '../../constants';

const CompanySettings = () => {
  const [formData, setFormData] = useState({
    dealerName: '',
    ownerName: '',
    mobileNo: '',
    email: '',
    dealerType: '',
    parentDealerId: '',
    logoUrl: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const { request, loading } = useApiRequest();

  const getPayload = () => {
    const payload = new FormData();
    payload.append('dealerName', formData.dealerName || '');
    
    if (logoFile) {
      payload.append('logo', logoFile);
    }
    
    return payload;
  };

  const fetchProfileData = async () => {
    await request({
      url: appApis.getProfile,
      method: "GET",
      showToast: false,
      onSuccess: (response) => {
        if (response?.data) {
          const profileData = response.data;
          setFormData({
            dealerName: profileData.dealerName || '',
            ownerName: profileData.ownerName || '',
            mobileNo: profileData.mobileNo || '',
            email: profileData.email || '',
            dealerType: profileData.dealerType || '',
            parentDealerId: profileData.parentDealerId || '',
            logoUrl: profileData.logoUrl || ''
          });
        }
      }
    });
  };

  const updateProfileData = () => {
    const payload = getPayload();
    
    request({
      url: appApis.getProfile,
      method: "PUT",
      body: payload,
      headers: { 'Content-Type': 'multipart/form-data' },
      showToast: true,
      onSuccess: (response) => {
        fetchProfileData();
      }
    });
  };

  const handleLogoUpload = (file) => {
    setLogoFile(file);
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };
  // Callback to handle file selection from input
  const uploadedFileImg = useMemo(() => {
    try {
      if (logoFile?.type?.startsWith('image/')) return URL.createObjectURL(logoFile);
      return null;
    } catch (error) {
      return null;
    }
  }, [logoFile]);

  const renderLogo = () => {
    if (!formData.logoUrl) {
      return (
        <div className="me-3 d-flex align-items-center justify-content-center bg-light rounded"
          style={{ width: '70px', height: '70px' }}>
          <span className="text-muted fw-bold">{formData.dealerName.slice(0, 1) || '?'}</span>
        </div>
      );
    }

    return (
      <div className="me-3">
        <img
          src={formData.logoUrl}
          alt="Company Logo"
          style={{ width: '100px', height: '100px', borderRadius: '50%' }}
        />
      </div>
    );
  };

  useEffect(() => {
    fetchProfileData();
  }, []);

  return (
    <div className="container-fluid p-4">
      <div className="row">
        <div className="col-12">
          <div className="card shadow-sm border-0 rounded-3">
            {/* Header */}
            <div className="card-header bg-white border-0 p-4">
              <div className="d-flex align-items-center">
                {renderLogo()}
                <div>
                  <h2 className="fw-bold mb-1">{formData.dealerName || "Name Not Set"}</h2>
                  <p className="text-muted mb-0">Manage your company information and settings</p>
                </div>
              </div>
            </div>

            {/* Form Content */}
            <div className="card-body p-4">
              <div className="row g-4">
                {/* Company Information */}
                <div className="col-md-6">
                  <InputElement
                    label="Dealer Name"
                    placeholder="Enter dealer name"
                    name="dealerName"
                    value={formData.dealerName}
                    onChange={e => handleInputChange('dealerName', e.target.value)}
                    isRequired={true}
                  />
                </div>
                <div className="col-md-6">
                  <InputElement
                    label="Owner Name"
                    placeholder="Enter owner name"
                    name="ownerName"
                    value={formData.ownerName}
                    onChange={e => handleInputChange('ownerName', e.target.value)}
                    isRequired={true}
                    disable={true}
                  />
                </div>

                {/* Contact Information */}
                <div className="col-md-6">
                  <InputElement
                    label="Mobile Number"
                    placeholder="Enter mobile number"
                    name="mobileNo"
                    value={formData.mobileNo}
                    onChange={e => handleInputChange('mobileNo', e.target.value)}
                    isRequired={true}
                    disable={true}
                  />
                </div>
                <div className="col-md-6">
                  <InputElement
                    disable={true}
                    label="Email Address"
                    placeholder="Enter email address"
                    name="email"
                    value={formData.email}
                    onChange={e => handleInputChange('email', e.target.value)}
                    isRequired={true}
                    type="email"
                  />
                </div>

                {/* Logo Section */}
                <div className="col-12">
                  <div className="border rounded p-3 bg-light">
                    <div className="row align-items-center">
                      <div className="col-md-8">
                        <Uploader
                          acceptType="image/*"
                          setData={handleLogoUpload}
                          label="Upload Company Logo"
                        />
                        <div className="text-muted small mt-2">
                          PNG, JPG up to 1MB.
                        </div>
                      </div>
                      <div className="col-md-4 text-center">
                        {logoFile && (
                          <div className="bg-white border rounded p-3" style={{
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            borderRadius: '8px'
                          }}>
                            <div className="text-center mb-2">
                              <small className="text-muted fw-medium">
                                New Upload
                              </small>
                            </div>
                            <div className="d-flex align-items-center justify-content-center" style={{
                              minHeight: '100px',
                              background: '#f8f9fa',
                              borderRadius: '6px',
                              border: '1px solid #e9ecef'
                            }}>
                              {uploadedFileImg ? (
                                <img
                                  src={uploadedFileImg}
                                  alt="Logo Preview"
                                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                                />
                              ) : (
                                <span className="text-muted">
                                  {logoFile?.name || 'File Selected'}
                                </span>
                              )}
                            </div>
                            <div className="text-center mt-2">
                              <span className="badge bg-primary" style={{ fontSize: '10px' }}>
                                New Upload
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="text-center mt-4 pt-3 border-top">
                <ButtonElement
                  text={loading ? "Saving..." : "Save Changes"}
                  onClick={updateProfileData}
                  variant="btn-primary"
                  cssClass="px-4 py-2"
                  disabled={loading}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CompanySettings;